import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { SearchInput } from "../../../components/ui/search-input";
import { Label } from "../../../components/ui/label";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, Edit2, CreditCard } from "lucide-react";
import { Loader } from "../../../components/Loader";
import { formatIndiaDateTime } from "../../../utils/dateFormat";
import { normalizeSearchQuery } from "../../../utils/search";

const PAYMENT_STATUSES = ["pending", "completed", "failed", "refunded"];
const PAYMENT_METHODS = ["cash", "upi", "card", "bank_transfer", "cheque", "other"];

export const PaymentsPage = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [totalPayments, setTotalPayments] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(true);
  const { authToken = "", id = "", username = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const [formData, setFormData] = useState({
    id: "",
    orderId: "",
    amount: "",
    status: "pending",
    method: "cash",
    paymentDate: "",
    notes: "",
    reference: "",
    createdBy: username || "",
  });

  const maxResults = 10;

  const fetchPayments = async (pageNum = 1, search = "") => {
    const firstResults = (pageNum - 1) * maxResults;
    const response = await axios.get(`${API_BASE_URL}account/${id}/payments`, {
      params: { firstResults, maxResults, search },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const result = await fetchPayments(page, appliedSearch);
      setPayments(result.payments || []);
      setTotalPayments(result.totalCount || 0);
    } catch (error) {
      console.error("Error fetching payments:", error);
      setPayments([]);
      setTotalPayments(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const q = normalizeSearchQuery(searchTerm);
      setAppliedSearch((prev) => {
        if (prev !== q) setPage(1);
        return q;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    loadPayments();
  }, [page, appliedSearch]);

  const resetForm = () => {
    setFormData({
      id: "",
      orderId: "",
      amount: "",
      status: "pending",
      method: "cash",
      paymentDate: "",
      notes: "",
      reference: "",
      createdBy: username || "",
    });
  };

  const openDrawerForCreate = () => {
    resetForm();
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const openDrawerForEdit = (payment: any) => {
    setFormData({
      id: payment.id,
      orderId: payment.orderId || "",
      amount: payment.amount || "",
      status: payment.status || "pending",
      method: payment.method || "cash",
      paymentDate: payment.paymentDate
        ? new Date(payment.paymentDate).toISOString().slice(0, 16)
        : "",
      notes: payment.notes || "",
      reference: payment.reference || "",
      createdBy: payment.createdBy || username || "",
    });
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      orderId: formData.orderId,
      amount: formData.amount,
      status: formData.status,
      method: formData.method || null,
      notes: formData.notes || null,
      reference: formData.reference || null,
      createdBy: formData.createdBy || username || String(id),
      paymentDate: formData.paymentDate
        ? new Date(formData.paymentDate).toISOString()
        : null,
    };

    try {
      if (isCreating) {
        await axios.post(`${API_BASE_URL}account/${id}/payment/create`, payload, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
      } else {
        await axios.put(
          `${API_BASE_URL}account/${id}/payment/update`,
          { ...payload, id: Number(formData.id) },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }
      setIsDrawerOpen(false);
      resetForm();
      await loadPayments();
    } catch (error) {
      console.error("Failed to save payment:", error);
      alert("Failed to save payment");
    }
  };

  const handleDelete = async (paymentId: number) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await axios.delete(`${API_BASE_URL}account/${id}/payment/${paymentId}/delete`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setPayments(payments.filter((p) => p.id !== paymentId));
      setTotalPayments((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to delete payment:", error);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalPayments / maxResults));

  if (loading && payments.length === 0) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="text-blue-600" size={32} />
            Payments
          </h1>
          <p className="text-gray-500 text-sm mt-1">Track order payments and receipts</p>
        </div>
        <Button
          onClick={openDrawerForCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 w-full sm:w-auto"
        >
          <Plus size={18} />
          New Payment
        </Button>
      </div>

      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <SearchInput
            placeholder="Search by order ID, status, method, or reference..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="pl-10 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            const q = normalizeSearchQuery(searchTerm);
            setAppliedSearch(q);
            setPage(1);
            loadPayments();
          }}
        >
          Search
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        {payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <CreditCard className="mx-auto mb-4 text-gray-300" size={48} />
            <p className="font-medium">No payments found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Order</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Method</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Reference</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{payment.orderId}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">₹{payment.amount}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium capitalize">
                        {payment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                      {(payment.method || "-").replace("_", " ")}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {payment.paymentDate
                        ? formatIndiaDateTime(payment.paymentDate, "-")
                        : "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{payment.reference || "-"}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => openDrawerForEdit(payment)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleDelete(payment.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing {payments.length} of {totalPayments}
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm text-gray-700">
            Page {page} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {isDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="w-full max-w-md h-full bg-white shadow-xl overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {isCreating ? "New Payment" : "Edit Payment"}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setIsDrawerOpen(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm text-gray-700 block mb-2">Order ID *</Label>
                <Input
                  value={formData.orderId}
                  onChange={(e) => setFormData({ ...formData, orderId: e.target.value })}
                  placeholder="e.g. 001"
                  required
                />
              </div>
              <div>
                <Label className="text-sm text-gray-700 block mb-2">Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <Label className="text-sm text-gray-700 block mb-2">Status *</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  {PAYMENT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm text-gray-700 block mb-2">Method</Label>
                <select
                  value={formData.method}
                  onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm text-gray-700 block mb-2">Payment Date</Label>
                <Input
                  type="datetime-local"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-sm text-gray-700 block mb-2">Reference</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="Txn / cheque ref"
                />
              </div>
              <div>
                <Label className="text-sm text-gray-700 block mb-2">Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setIsDrawerOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                  {isCreating ? "Create" : "Update"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
